// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — what each AYNI XR plan includes (`docs/AYNI_XR_PRODUCTS.md`): the
 * one table the plans page, the "Your plan" panel and the site's server
 * read, so a plan's contents change here and no licence is reissued.
 *
 * A licence (`ayni-xr`, issued by the AYNI licence server) carries its plan
 * as one feature, `xr-plan-<id>`, and its streaming packages as `xr-…`
 * names (or `*` for all). Everything else — hosting and its size, live,
 * activities, Khipus, data tools, branding, credits — follows from the plan
 * here.
 *
 * Prices in euros excluding VAT (Plus: VAT included, `vatIncluded`); `null`
 * means by quote. AYNI (owner, 16–17 October 2026, the pricing review;
 * `docs/AYNI_XR_PRICING.md`): Researcher academic €390 / commercial €849,
 * Teacher €249 and School €1,499 with every stream, Plus €99 a year VAT
 * included (Live Earth and the climate highlights), Media credits 1,200 /
 * 10,000 a year. Shapes only: no DOM, imported by the browser and by Pages
 * Functions.
 */

/** XR's streaming packages: its own, not AYNI 2D's or 3D's (owner, 2 October 2026). */
export const XR_PACKAGES = ['xr-atmosphere', 'xr-ocean', 'xr-climate', 'xr-fire-aq'] as const
export type XrPackage = (typeof XR_PACKAGES)[number]
/** Disaster alerts, earthquakes, events: in "all", and in every Situation Room plan. */
export const XR_HAZARDS = 'xr-hazards'

export type XrSegment = 'situation' | 'research' | 'education' | 'public' | 'media' | 'venue'
export const XR_SEGMENTS: readonly XrSegment[] = ['situation', 'research', 'education', 'public', 'media', 'venue']

export type XrPlanId =
  | 'plus'
  | 'teacher'
  | 'school'
  | 'district'
  | 'researcher'
  | 'lab'
  | 'department'
  | 'situation-team'
  | 'situation-team-plus'
  | 'situation-org'
  | 'media-desk'
  | 'newsroom'
  | 'venue'

/**
 * How a plan gets streams: every package, or (Plus, owner 2026-10-17) Live
 * Earth and the climate highlights: every real-time stream and forecast,
 * and the hand-picked records in `PLUS_HIGHLIGHTS`; Free, none.
 */
export type PackageAccess = 'all' | 'all-hazards' | 'live-highlights' | 'none'

/** Plus's streams, as package names no licence carries: Live Earth, and the climate highlights. */
export const XR_LIVE = 'xr-live'
export const XR_HIGHLIGHTS = 'xr-highlights'
/**
 * The stream host's package of the streams only Education plays
 * (`EDUCATION_ONLY_STREAMS`; manifest `packages/xr-education.json`). The host
 * restricts it (owner, 2026-11-03; AYNI-2D gateway `RESTRICTED_PACKAGES`):
 * `'*'` does not cover it, so a stream token, a meeting's grant or a licence
 * opens those streams only when it names this package.
 */
export const XR_EDUCATION = 'xr-education'
/** Packages the stream host never counts under `'*'`: kept beside `'*'` wherever grants are collapsed or merged. */
export const RESTRICTED_STREAM_PACKAGES: readonly string[] = [XR_EDUCATION]

/**
 * The climate highlights Plus plays (owner, approved 2026-10-17, the
 * pricing review's Plus tab): 27 yearly records and projections, by the
 * live index's stable `id` (a publisher's renaming leaves it alone), the
 * index `name` beside each.
 */
export const PLUS_HIGHLIGHTS: ReadonlySet<string> = new Set([
  'e1b3727e39f60146', // cmip6_tas_anomaly_ssp126
  '7a7303ae451bcf27', // cmip6_tas_anomaly_ssp585
  'b944cf7d57efd5fb', // cmip6_tx35ba_days_ssp126
  'e607da7e5423f955', // cmip6_tx35ba_days_ssp585
  'a76dbdf7c6037559', // cmip6_pr_change_ssp126
  '12eb32726a08261c', // cmip6_pr_change_ssp585
  '97362bf9daf96803', // koppen_geiger_ssp126
  'd9f7b59ddd349700', // koppen_geiger_ssp585
  'f1a884fa1c5dab83', // cmip6_siconc_ssp126
  '862fd1f27d0b3303', // cmip6_siconc_ssp585
  'a659c172b21f5b6a', // ar6_sea_level_rise_ssp126
  '7f3a9e47996c29ac', // ar6_sea_level_rise_ssp585
  'c367c26d9a960c10', // cmip6_sst_change_ssp126
  'fc38c8f52916e629', // cmip6_sst_change_ssp585
  '5dbec219da6c9e07', // noaaglobaltemp_anomaly_yearly
  '53a92174afaae9f7', // era5_t2m_change_yearly
  '5e0e5b3606b8fcb5', // mann2009_temp_recon_yearly
  '2154adee8b0dd889', // cams_xco2_yearly
  '91d7e45a80c58388', // cams_xch4_yearly
  'af94907d882fa090', // osisaf_sea_ice_yearly
  'f54bf38fdce712d4', // c3s_sea_level_change_yearly
  '68a10bdf12c17d96', // cmems_ph_change_yearly
  '46f578d2b5e8bc8d', // modis_burned_area_yearly
  '9f7737036909303a', // ghsl_population_density_yearly
  'f4c0d64166c31171', // tropical_cyclones_yearly
  'fefe4414e49eb1fc', // usgs_earthquakes_yearly
  'fbe6024793e0aab0', // goes_generations_bw
])

export interface XrPlan {
  id: XrPlanId
  segment: XrSegment
  /** Price per year in euros (before VAT unless `vatIncluded`); null: by quote. Researcher: the academic price. */
  yearly: number | null
  /** Plus: a month's price, sold online only. */
  monthly?: number
  /** Plus (owner, 2026-10-17): the prices include VAT. */
  vatIncluded?: boolean
  /** Researcher and Lab (owner, 2026-10-17/18): the price for commercial use; `yearly` is the academic one. */
  commercialYearly?: number
  /**
   * Researcher and Lab (owner, 2026-10-17/18): the prices with own data
   * streaming, academic and commercial, shown as coming soon until own data
   * is on; until then the plan is sold at `yearly` / `commercialYearly`,
   * and own data comes as the `xr-cap-own-data` add-on.
   */
  ownDataYearly?: { academic: number; commercial: number }
  /** "From": the yearly price is where quotes start. */
  from?: boolean
  /** Named users (seats); null: as quoted. */
  seats: number | null
  /** Most people in a meeting this plan hosts; 0: it hosts none. */
  meetings: number
  /** Most people in an in-person room this plan hosts. */
  rooms: number
  /** Values, pins, graphs and export on the data. */
  data: boolean
  packages: PackageAccess
  live: boolean
  activities: boolean
  /**
   * Khipus: open, play and download them, and save a meeting as one
   * (owner, 2026-10-07: every plan for now, decided per plan later; this
   * is the one switch). Keeping them on the account is `khipuRetention`.
   */
  khipus: boolean
  /** Logo, lower third and clean output for keying. */
  branding: boolean
  /** Render credits granted each year. */
  credits: number
  /** The AYNI chatbot (owner, 2026-10-03: paid plans only; `docs/AYNI_CHATBOT_XR_BRIEF.md`). */
  chatbot: boolean
  /**
   * How far the data tools go (with `data`): `basic` is values, pins, the
   * graph, Line and Box; `full` adds statistics over a whole region (the
   * dataset, a latitude band, a named region, the view).
   */
  analysis: AnalysisLevel
  exports: ExportLevel
  /** Most globes side by side (the device may allow fewer); with one, the View tab has no layout row (owner, 2026-10-17). */
  globes: number
  /** The View tab's colour palette, invert and limits (owner, 2026-10-17: not on Free or Plus). */
  palette: boolean
  /**
   * Anomalies against a held frame or a climatology, with the publication
   * figures and methods (PNG 300 dpi, SVG): Research and Situation plans,
   * and Media on their own (owner, 2026-10-17), without trend tests,
   * comparisons, records or lab space.
   */
  anomalies: boolean
  /**
   * Education (owner, 2026-10-17): the research tools are not shown at all
   * rather than shown locked, and the bar's Analyze button and the contours
   * are gone (the Tools tab keeps values, pins, Line, Box and statistics).
   */
  teaching: boolean
  /** The lesson library and its player (Education). */
  lessons: boolean
  /** Class sessions, and lending the presenter's data tools to the class (Education). */
  classroom: boolean
  /**
   * Games in the plan's meetings (`docs/AYNI_XR_MEETING_GAMES_PLAN.md`):
   * tables of four, funny names, the room keeping the score. Education only
   * (owner, 2026-10-10, decision D3); any other plan through `xr-cap-games`.
   */
  games: boolean
  /**
   * AYNI (owner, 2026-11-03, `docs/AYNI_XR_MEETING_STAGE_PLAN.md` ST5): the
   * meeting stage — the presenter and up to five more on camera and
   * microphone in the meetings the plan hosts. Included in the big plans;
   * an add-on (`xr-cap-stage`, `STAGE_ADDON_YEARLY`) on Teacher, School,
   * Researcher and Lab.
   */
  stage: boolean
  /**
   * How long a saved Khipu is kept: days, or `licence` (until the licence
   * ends plus `KHIPU_GRACE_DAYS`); null: none kept, download only.
   */
  khipuRetention: number | 'licence' | null
  /** Days a saved Khipu outlives the licence that saved it (Plus 7, others `KHIPU_GRACE_DAYS`). */
  khipuGraceDays: number
  /** Most Khipus an account keeps (the plans that keep them: 200; Plus kept 20 until 2026-10-17, now none). */
  khipuMax: number
  /**
   * Megabytes of stream media a licence may load in a UTC day; null: no cap.
   * Plus: 2 GB (what Plus with every package had, owner 2026-10-06);
   * Education: 10 GB (D13).
   */
  streamDailyMB: number | null
  /** Most pins at once (with `data`). */
  pins: number
  /** Most real-time overlays at once; null: no plan limit. */
  overlays: number | null
  /** The Line and Box measuring tools (with `data`); Plus measures values and pins only. */
  lineBox: boolean
  /** Chatbot questions a day (the server's `AYNI_CHAT_DAILY_REQUESTS` stays the ceiling). */
  chatDaily: number
  /**
   * The research tools (docs/AYNI_XR_RESEARCH_TIER_PLAN.md): a frame held
   * as a baseline and every later frame read as a difference from it, the
   * difference between globes, a line followed over time. Research and
   * Situation plans (decision R2, the default until the owner decides).
   */
  research: boolean
  /**
   * Own data (docs/AYNI_DATA_INTAKE_PLAN.md, phase 1): the account may send
   * its own files to the intake server and play them as private streams.
   * Situation Room plans (owner, 2026-10-18); Researcher and Lab through
   * the `xr-cap-own-data` add-on (owner, 2026-10-17/18), as any other
   * plan. Quotas: `src/services/ownData.ts`.
   */
  ownData: boolean
}

export type AnalysisLevel = 'basic' | 'full'
/** Which CSV downloads: none, the pins' series only, or every analysis export too. */
export type ExportLevel = 'none' | 'pins' | 'all'

/** Days a Khipu outlives the licence that saved it, so its owner can download it. */
export const KHIPU_GRACE_DAYS = 30

/** What everyone has without a plan: the SOS catalogue, joining anything, small rooms with friends. */
export const FREE: Omit<XrPlan, 'id' | 'segment' | 'yearly' | 'seats'> = {
  meetings: 0,
  rooms: 5,
  data: false,
  packages: 'none',
  live: false,
  activities: false,
  khipus: true,
  branding: false,
  credits: 0,
  chatbot: false,
  analysis: 'basic',
  exports: 'pins',
  // One globe (owner, 2026-10-05: the ladder's Free row).
  globes: 1,
  palette: false,
  anomalies: false,
  teaching: false,
  lessons: false,
  classroom: false,
  games: false,
  stage: false,
  khipuRetention: null,
  khipuGraceDays: KHIPU_GRACE_DAYS,
  khipuMax: 0,
  streamDailyMB: null,
  pins: 0,
  overlays: null,
  lineBox: false,
  chatDaily: 0,
  research: false,
  ownData: false,
}

/** Most Khipus an account keeps on the plans that keep them (before the Plus plan: every plan). */
export const KHIPU_MAX_DEFAULT = 200
/** Pins the full data tools allow: the measuring tools' own ceiling (`MAX_PINS` in measure/pins.ts, 6 since 2026-10-09). */
export const FULL_PINS = 6
/** Chatbot questions a day on the paid plans (the server's default ceiling). */
export const CHAT_DAILY_DEFAULT = 200

/**
 * Presenting plans (Venue; the base of the others): Line, Box and every CSV
 * export, but no statistics over a whole region, which Research, Education,
 * Media and Situation plans add (owner, 2026-10-05, research decision R7;
 * Media and Situation since 2026-10-17/18).
 */
const presenting = {
  live: true, activities: true, khipus: true, data: true, rooms: 40, branding: false, credits: 0, chatbot: true,
  analysis: 'basic', exports: 'all', globes: 4, lessons: false, classroom: false, games: false, stage: false, khipuRetention: 'licence',
  research: false, ownData: false, palette: true, anomalies: false, teaching: false,
  khipuGraceDays: KHIPU_GRACE_DAYS, khipuMax: KHIPU_MAX_DEFAULT, streamDailyMB: null, pins: FULL_PINS, overlays: null,
  lineBox: true, chatDaily: CHAT_DAILY_DEFAULT,
} as const

/** Research plans: everything presenting has, statistics over any region, and the research tools (R2, R7). */
const researching = { ...presenting, analysis: 'full', research: true, anomalies: true } as const

/**
 * Media (owner, 2026-10-17): statistics over a whole region, anomalies
 * against a climatology and the publication figures, the numbers
 * journalists quote; no trend tests, comparisons, records or lab space.
 */
const reporting = { ...presenting, analysis: 'full', anomalies: true, branding: true } as const

/**
 * Situation Room plans (owner, 2026-10-18: "they are the biggest clients,
 * they get everything"): every tool the app has, whole-region statistics,
 * the research tools, own data (when it is on), the lessons and class
 * tools, games and branding. Not render credits, which Media plans buy.
 */
const situating = {
  ...presenting, analysis: 'full', research: true, anomalies: true, ownData: true,
  lessons: true, classroom: true, games: true, branding: true, stage: true,
} as const

/**
 * Education (owner, 2026-10-05): the lesson library and class tools; Line,
 * Box and statistics over a whole region (decision R7b, the same day:
 * whole-region statistics are for Research and Education), pin exports
 * only, and Khipus kept 90 days unless the licence carries
 * `xr-cap-khipu-keep`. Every stream since 2026-10-17 (no add-ons), and
 * `teaching`: no research tools shown, no contours, no bar Analyze button.
 * Four globes since 2026-10-26 (owner; 2 before), as a class already lent.
 */
const educating = { ...presenting, analysis: 'full', exports: 'pins', globes: 4, lessons: true, classroom: true, games: true, khipuRetention: 90, streamDailyMB: 10_000, teaching: true } as const

/**
 * Plus (owner, 2026-10-06, reworked 2026-10-17; `docs/AYNI_XR_PRICING.md`):
 * one person, personal non-commercial use, a way to learn the planet. Live
 * Earth (every real-time stream and forecast) and the climate highlights
 * under a daily volume cap, values and 2 pins with the graph, no Line, Box,
 * statistics or CSV, one globe, one real-time overlay, 20 chatbot
 * questions a day, Khipus played but not kept, no palette. It hosts
 * nothing beyond Free's rooms of 5.
 */
const personal = {
  ...FREE, data: true, packages: 'live-highlights', chatbot: true, analysis: 'basic', exports: 'none', globes: 1,
  khipuRetention: null, khipuGraceDays: 7, khipuMax: 0, streamDailyMB: 2_000,
  pins: 2, overlays: 1, lineBox: false, chatDaily: 20,
} as const

export const XR_PLANS: Readonly<Record<XrPlanId, XrPlan>> = {
  'situation-team': { id: 'situation-team', segment: 'situation', yearly: 7500, seats: 10, meetings: 500, packages: 'all-hazards', ...situating },
  // AYNI (owner, decision CR-D11, 2026-10-11): Team Plus for a buyer's direct purchase, under Spain's €15,000 minor-contract limit; Organisation from €24,000 (the price check, docs/AYNI_XR_SITUATION_ROOM_PRICE_BENCHMARK.md).
  'situation-team-plus': { id: 'situation-team-plus', segment: 'situation', yearly: 14500, seats: 25, meetings: 500, packages: 'all-hazards', ...situating },
  'situation-org': { id: 'situation-org', segment: 'situation', yearly: 24000, from: true, seats: null, meetings: 500, packages: 'all-hazards', ...situating },
  researcher: { id: 'researcher', segment: 'research', yearly: 390, commercialYearly: 849, ownDataYearly: { academic: 490, commercial: 999 }, seats: 1, meetings: 100, packages: 'all', ...researching },
  // AYNI (owner, 2026-10-17/18): Researcher and Lab at academic and commercial prices; own data, once it is on, as an add-on (`xr-cap-own-data`) at the `ownDataYearly` totals.
  lab: { id: 'lab', segment: 'research', yearly: 1490, commercialYearly: 3490, ownDataYearly: { academic: 1890, commercial: 4499 }, seats: 5, meetings: 100, packages: 'all', ...researching },
  // AYNI (owner, 2026-11-03, stage plan ST5): the big plans include the meeting stage (Situation's three by `situating`; Department, District, Newsroom, Venue here).
  department: { id: 'department', segment: 'research', yearly: null, seats: null, meetings: 100, packages: 'all', ...researching, stage: true },
  // AYNI (owner, 2026-10-26): classes of 40 (Teacher), 100 (School) and 250 (District); all were 40.
  teacher: { id: 'teacher', segment: 'education', yearly: 249, seats: 1, meetings: 40, packages: 'all', ...educating },
  school: { id: 'school', segment: 'education', yearly: 1499, seats: 10, meetings: 100, packages: 'all', ...educating },
  district: { id: 'district', segment: 'education', yearly: null, seats: null, meetings: 250, packages: 'all', ...educating, stage: true },
  plus: { id: 'plus', segment: 'public', yearly: 99, monthly: 11.99, vatIncluded: true, seats: 1, ...personal },
  'media-desk': { id: 'media-desk', segment: 'media', yearly: 990, seats: 1, meetings: 100, packages: 'all', ...reporting, credits: 1200 },
  newsroom: { id: 'newsroom', segment: 'media', yearly: 6900, seats: 10, meetings: 100, packages: 'all', ...reporting, credits: 10000, stage: true },
  venue: { id: 'venue', segment: 'venue', yearly: 2400, seats: 5, meetings: 200, packages: 'all', ...presenting, stage: true },
}

/**
 * AYNI (owner, 2026-11-03, stage plan ST6): the meeting stage as an add-on,
 * euros a year before VAT, on the plans that do not include it. Sold by hand
 * for now (the key added on the licence admin page) until Paddle has an
 * add-on step; Plus and Media Desk are not offered it.
 */
export const STAGE_ADDON_YEARLY: Readonly<Partial<Record<XrPlanId, number>>> = {
  teacher: 99,
  researcher: 99,
  school: 249,
  lab: 249,
}

/** How many people besides the presenter the stage holds (ST7: six in all). */
export const STAGE_MAX_MEMBERS = 5

/**
 * The segments shown to buyers (the plans dialog, `/pricing`). Venue is
 * kept for licences already issued but not shown (owner, 2026-10-18:
 * "nothing public yet").
 */
export const PUBLIC_SEGMENTS: readonly XrSegment[] = XR_SEGMENTS.filter((s) => s !== 'venue')

/**
 * Streams only Education plans play (owner, 2026-10-18), by the live
 * index's stable id: Global Fishing Watch's data (CC BY-NC 4.0;
 * `docs/AYNI_XR_MEDIA_RIGHTS_AUDIT.md`). GFW confirmed on 8 October 2026
 * that educational use needs no commercial licence (Leah Buckley); vessel
 * traffic joined fishing effort here on 20 October 2026 (owner).
 */
export const EDUCATION_ONLY_STREAMS: ReadonlySet<string> = new Set([
  'f7ad25d3468d4dd7', // gfw_fishing_effort
  '17535c02e13e2722', // gfw_traffic
])

/** The plans of a segment, in the order the plans page shows them. */
export function plansOf(segment: XrSegment): XrPlan[] {
  return Object.values(XR_PLANS).filter((p) => p.segment === segment)
}

/**
 * Render credits bought on top of a Media plan (owner, 2026-10-17): 1,000
 * for €99 before VAT, online once online payment is live. Unused credits
 * roll over for `CREDIT_ROLLOVER_MONTHS`, never more than a year's
 * allowance, and the balance is shown in minutes.
 */
export const CREDIT_PACK = { credits: 1000, eur: 99 } as const
/** Months unused credits are kept (owner, 2026-10-17). */
export const CREDIT_ROLLOVER_MONTHS = 12
/** What each render costs in credits (owner, 8 October 2026); keepForLicence keeps one video until its licence ends. */
export const CREDIT_COST = { still: 1, hd1080PerMinute: 10, uhd4kPerMinute: 30, uhd8kPerMinute: 80, keepForLicence: 1 } as const

export interface QuoteLine {
  /** What the line is: the plan (no packages are sold on top since 2026-10-17). */
  kind: 'plan'
  amount: number
}

export interface Quote {
  lines: QuoteLine[]
  /** Yearly total in euros, or null when the plan is by quote. */
  total: number | null
}

/** Who a Researcher licence is for (owner, 2026-10-17): the price differs, the licence does not. */
export type PlanUse = 'academic' | 'commercial'

/**
 * What a plan costs a year. Every plan includes its streams (no packages are
 * sold on top since 2026-10-17); a Researcher for commercial use pays its
 * commercial price.
 */
export function quote(planId: XrPlanId, use: PlanUse = 'academic'): Quote {
  const plan = XR_PLANS[planId]
  if (plan.yearly === null) return { lines: [], total: null }
  const amount = use === 'commercial' && plan.commercialYearly !== undefined ? plan.commercialYearly : plan.yearly
  return { lines: [{ kind: 'plan', amount }], total: amount }
}

// ------------------------------------------------------------ entitlements

/** What this browser may do: from a licence, or the free defaults. */
export interface XrEntitlements {
  plan: XrPlan | null
  /** Packages this browser may stream; 'all' for every one. */
  packages: ReadonlySet<string> | 'all'
  meetings: number
  rooms: number
  data: boolean
  live: boolean
  activities: boolean
  khipus: boolean
  branding: boolean
  credits: number
  chatbot: boolean
  analysis: AnalysisLevel
  exports: ExportLevel
  globes: number
  /** The View tab's colour palette (not on Free or Plus). */
  palette: boolean
  /** Anomalies against a frame or a climatology, figures and methods (Research, Situation, Media). */
  anomalies: boolean
  /** Education: research tools not shown, no contours, no bar Analyze button. */
  teaching: boolean
  lessons: boolean
  classroom: boolean
  /** Games in the meetings it hosts (Education; `xr-cap-games` on other plans). */
  games: boolean
  /** The meeting stage in the meetings it hosts (the big plans; `xr-cap-stage` on others). */
  stage: boolean
  khipuRetention: number | 'licence' | null
  khipuGraceDays: number
  khipuMax: number
  /** Megabytes of stream media a day; null: no cap. */
  streamDailyMB: number | null
  pins: number
  overlays: number | null
  lineBox: boolean
  chatDaily: number
  research: boolean
  /** Own data: private uploads and streams (`xr-cap-own-data` on other plans). */
  ownData: boolean
  /** The capability keys on the licence that added to the plan, for "Your plan". */
  caps: readonly XrCapability[]
}

/**
 * Capability keys a licence can carry as features (`xr-cap-<name>`), each
 * with its own end date. They only add to the plan's defaults (owner,
 * 2026-10-05): what a licence gives is the plan plus every key on it, so a
 * custom deal or a trial needs no code. Tiers take the highest present.
 */
export const XR_CAPABILITIES = [
  'xr-cap-data',
  'xr-cap-analysis-full',
  'xr-cap-exports-all',
  'xr-cap-globes-4',
  'xr-cap-lessons',
  'xr-cap-classroom',
  'xr-cap-chatbot',
  'xr-cap-live',
  'xr-cap-host-40',
  'xr-cap-host-100',
  'xr-cap-host-500',
  'xr-cap-khipu-keep',
  'xr-cap-research',
  'xr-cap-own-data',
  'xr-cap-games',
  'xr-cap-stage',
] as const
export type XrCapability = (typeof XR_CAPABILITIES)[number]

/** The known capability keys a licence's features hold that are still current at `nowS` (seconds). */
export function capabilitiesFromFeatures(features: Readonly<Record<string, number>>, nowS: number): XrCapability[] {
  return XR_CAPABILITIES.filter((cap) => {
    const until = Object.prototype.hasOwnProperty.call(features, cap) ? features[cap] : undefined
    return typeof until === 'number' && until > nowS
  })
}

const PLAN_FEATURE = /^xr-plan-([a-z-]+)$/

/** The plan a licence's features name, when it is a known one and still current at `nowS` (seconds). */
export function planFromFeatures(features: Readonly<Record<string, number>>, nowS: number): XrPlan | null {
  for (const [name, until] of Object.entries(features)) {
    const id = PLAN_FEATURE.exec(name)?.[1]
    if (id && Object.prototype.hasOwnProperty.call(XR_PLANS, id) && until > nowS) return XR_PLANS[id as XrPlanId]
  }
  return null
}

/**
 * Entitlements from a licence's plan, packages and capability keys, or the
 * free ones for a null plan. Keys apply only beside a current plan: the
 * licence server issues none without one, and an expired plan gives nothing.
 */
export function entitlementsFrom(plan: XrPlan | null, packages: readonly string[] = [], caps: readonly string[] = []): XrEntitlements {
  const base = plan ?? FREE
  const allPackages = plan !== null && (plan.packages === 'all' || plan.packages === 'all-hazards' || (plan.packages !== 'live-highlights' && packages.includes('*')))
  // Plus (owner, 2026-10-17): Live Earth and the highlights, whatever packages an older Plus licence names.
  const ownPackages: ReadonlySet<string> | 'all' = allPackages
    ? 'all'
    : plan?.packages === 'live-highlights'
      ? new Set([XR_LIVE, XR_HIGHLIGHTS])
      : new Set(packages.filter((p) => (XR_PACKAGES as readonly string[]).includes(p) || p === XR_HAZARDS))
  const held = plan === null ? [] : XR_CAPABILITIES.filter((c) => caps.includes(c))
  const has = (c: XrCapability) => held.includes(c)
  const hostTier = has('xr-cap-host-500') ? 500 : has('xr-cap-host-100') ? 100 : has('xr-cap-host-40') ? 40 : 0
  return {
    plan,
    packages: ownPackages,
    meetings: Math.max(base.meetings, hostTier),
    rooms: base.rooms,
    data: base.data || has('xr-cap-data'),
    live: base.live || has('xr-cap-live'),
    activities: base.activities,
    khipus: base.khipus,
    branding: base.branding,
    credits: base.credits,
    chatbot: base.chatbot || has('xr-cap-chatbot'),
    analysis: has('xr-cap-analysis-full') ? 'full' : base.analysis,
    exports: has('xr-cap-exports-all') ? 'all' : base.exports,
    globes: has('xr-cap-globes-4') ? Math.max(base.globes, 4) : base.globes,
    palette: base.palette,
    anomalies: base.anomalies || has('xr-cap-research'),
    teaching: base.teaching,
    lessons: base.lessons || has('xr-cap-lessons'),
    classroom: base.classroom || has('xr-cap-classroom'),
    games: base.games || has('xr-cap-games'),
    stage: base.stage || has('xr-cap-stage'),
    khipuRetention: has('xr-cap-khipu-keep') ? 'licence' : base.khipuRetention,
    khipuGraceDays: base.khipuGraceDays,
    khipuMax: base.khipuMax,
    streamDailyMB: base.streamDailyMB,
    // `xr-cap-data` gives a plan the full data tools: every pin, Line and Box.
    pins: has('xr-cap-data') ? Math.max(base.pins, FULL_PINS) : base.pins,
    overlays: base.overlays,
    lineBox: base.lineBox || has('xr-cap-data') || has('xr-cap-analysis-full'),
    chatDaily: has('xr-cap-chatbot') ? Math.max(base.chatDaily, CHAT_DAILY_DEFAULT) : base.chatDaily,
    research: base.research || has('xr-cap-research'),
    ownData: base.ownData || has('xr-cap-own-data'),
    caps: held,
  }
}

/**
 * Until when a Khipu saved at `savedAtMs` is kept, in milliseconds, or null
 * when the plan keeps none. Never past the licence's end plus its grace
 * days (`KHIPU_GRACE_DAYS` unless the plan says less), whatever the plan says.
 */
export function khipuKeptUntil(retention: XrEntitlements['khipuRetention'], savedAtMs: number, licenceEndMs: number, graceDays: number = KHIPU_GRACE_DAYS): number | null {
  if (retention === null) return null
  const ceiling = licenceEndMs + graceDays * 86_400_000
  if (retention === 'licence') return ceiling
  return Math.min(savedAtMs + retention * 86_400_000, ceiling)
}

/**
 * AYNI (decision M1, 2026-10-10; `docs/AYNI_XR_MULTIPLE_LICENCES_PLAN.md`):
 * what an account keeps whichever of its licences is in use, the best of
 * them all. Everything else (streams, tools, globes, meetings, a Lab, own
 * data, credits) follows the licence in use.
 */
export interface PersonalAllowances {
  chatbot: boolean
  /** Chatbot questions a day: the most any licence with the chatbot gives (Plus 20, others 200). */
  chatDaily: number
  /** The longest keeping any licence gives a Khipu saved now. */
  khipuRetention: XrEntitlements['khipuRetention']
  /** The end (ms) of the licence that keeps Khipus longest, for `khipuKeptUntil`; null when none keeps them. */
  khipuLicenceEndMs: number | null
  /** That licence's grace days after its end (Plus 7, others `KHIPU_GRACE_DAYS`). */
  khipuGraceDays: number
  /** Most Khipus kept: the most any licence that keeps them allows (Plus 20). */
  khipuMax: number
}

/** The best of what each held licence grants (`entitlements` from its token, `endMs` its end), judged at `nowMs`. */
export function personalAllowances(held: readonly { entitlements: XrEntitlements; endMs: number }[], nowMs: number): PersonalAllowances {
  let best: { retention: XrEntitlements['khipuRetention']; endMs: number; until: number; graceDays: number } | null = null
  let khipuMax = 0
  for (const { entitlements, endMs } of held) {
    const until = khipuKeptUntil(entitlements.khipuRetention, nowMs, endMs, entitlements.khipuGraceDays)
    if (until === null) continue
    khipuMax = Math.max(khipuMax, entitlements.khipuMax)
    if (best === null || until > best.until) best = { retention: entitlements.khipuRetention, endMs, until, graceDays: entitlements.khipuGraceDays }
  }
  const chatting = held.filter((h) => h.entitlements.chatbot)
  return {
    chatbot: chatting.length > 0,
    chatDaily: Math.max(0, ...chatting.map((h) => h.entitlements.chatDaily)),
    khipuRetention: best?.retention ?? null,
    khipuLicenceEndMs: best?.endMs ?? null,
    khipuGraceDays: best?.graceDays ?? KHIPU_GRACE_DAYS,
    khipuMax,
  }
}

/** The licence in use's entitlements with the account's personal allowances in place of its own. */
export function withPersonalAllowances(inUse: XrEntitlements, allowances: PersonalAllowances): XrEntitlements {
  return {
    ...inUse,
    chatbot: allowances.chatbot,
    chatDaily: allowances.chatDaily,
    khipuRetention: allowances.khipuRetention,
    khipuGraceDays: allowances.khipuGraceDays,
    khipuMax: allowances.khipuMax,
  }
}
